import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, Send, Sparkles } from 'lucide-react';
import { api } from '../lib/api';

const SUGGESTIONS = [
  "Which projects are over budget?",
  "Show all pending invoices > AED 40K",
  "Summarize my portfolio performance",
  "What are the biggest risks right now?",
];

export default function AIChat({ onClose }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [msgs, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput('');
    setMsgs((m) => [...m, { role: 'user', content: q }]);
    setLoading(true);
    try {
      const r = await api.post('/ai/chat', { message: q, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMsgs((m) => [...m, { role: 'assistant', content: r.data.answer }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: 'assistant', content: 'Sorry — AI is unavailable right now.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end md:items-center md:justify-end p-0 md:p-6"
      onClick={onClose}
      data-testid="ai-chat-panel"
    >
      <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" />
      <motion.div
        initial={{ x: 50, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 50, opacity: 0 }}
        transition={{ type: 'spring', damping: 24 }}
        onClick={(e) => e.stopPropagation()}
        className="relative glass-strong w-full md:w-[440px] h-[80vh] md:h-[720px] max-h-[95vh] rounded-t-3xl md:rounded-3xl flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="p-5 border-b border-white/40 flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#A6C4B8] to-[#679286] grid place-items-center text-white shadow-lg">
            <Sparkles size={18} />
          </div>
          <div className="flex-1">
            <div className="font-serif text-lg">Iris AI</div>
            <div className="text-xs text-slate-500">Iris AI</div>
          </div>
          <button onClick={onClose} data-testid="ai-chat-close" className="p-2 rounded-full hover:bg-white/60">
            <X size={18} />
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
          {msgs.length === 0 && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600 leading-relaxed">
                Ask me anything about your portfolio, deals, BOQs, approvals, or vendors. I have live context on everything.
              </div>
              <div className="space-y-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => send(s)}
                          data-testid={`ai-suggest-${i}`}
                          className="w-full text-left text-sm px-3 py-2.5 bg-white/60 hover:bg-white/90 rounded-2xl transition-all border border-white/70">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${
                m.role === 'user'
                  ? 'bg-[#1A2530] text-white'
                  : 'bg-white/85 text-slate-800 border border-white/70'
              }`}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="px-4 py-2.5 rounded-2xl bg-white/85 border border-white/70 text-sm">
                <span className="ai-caret" />
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-4 border-t border-white/40">
          <div className="flex items-center gap-2 bg-white/80 rounded-full p-1.5 border border-white/70">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="Ask about your portfolio or how any feature works…"
              className="flex-1 bg-transparent px-3 text-sm outline-none"
              data-testid="ai-chat-input"
              disabled={loading}
            />
            <button onClick={() => send()} disabled={loading || !input.trim()}
                    data-testid="ai-chat-send"
                    className="w-9 h-9 rounded-full bg-[#1A2530] text-white grid place-items-center disabled:opacity-40 hover:bg-[#2C3E50] transition-all">
              <Send size={14} />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
